import { Dialog, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { Order, OrderStatus, PaymentMethods } from '@api/Orders/Order';
import OrdersService from '@api/Orders/OrderCtrl';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { PaymentMethod, getOrderPaidBy } from '@api/Orders/OrderPayments';
import { currencyFormatter } from '@utils/Numbers';
interface ModalListOrdersProps {
    modalListOrders: OpenModalHookType;
    paymentMethodType: PaymentMethods;
    startDate: Date;
    endDate: Date;
    /** 'all' = todas las sucursales; sin él usa la sucursal en operación */
    branchOfficeId?: string;
}
export const ModalListOrders: React.FunctionComponent<ModalListOrdersProps> = (props: any) => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const { modalListOrders, paymentMethodType, startDate, endDate, branchOfficeId } = props;
    const ordersService = useMemo(() => new OrdersService(), []);
    const [orders, setOrders] = useState<Order[]>([]);

    const handleClose = () => {
        modalListOrders.toggle();
    };

    const getData = async () => {
        showLoader();
        try {
            const effectiveBranchId = branchOfficeId ?? branchOfficeSelected?.id;
            if (effectiveBranchId) {
                let ordersData = await ordersService.getOrders(
                    OrderStatus.Completed,
                    startDate,
                    endDate,
                    effectiveBranchId !== 'all' ? effectiveBranchId : undefined
                );
                switch (paymentMethodType) {
                    case PaymentMethods.Cash:
                        ordersData = ordersData.filter((order) => getOrderPaidBy(order, PaymentMethod.Cash) > 0);
                        break;
                    case PaymentMethods.CreditCard:
                        ordersData = ordersData.filter((order) => getOrderPaidBy(order, PaymentMethod.CreditCard) > 0);
                        break;
                    case PaymentMethods.BankTransfer:
                        ordersData = ordersData.filter(
                            (order) => getOrderPaidBy(order, PaymentMethod.BankTransfer) > 0
                        );
                        break;
                }
                setOrders(ordersData);
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        if (modalListOrders.modalOpen) {
            void getData();
        }
    }, [modalListOrders.modalOpen]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 25, padding: 4 }
            }}
            open={modalListOrders.modalOpen}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {paymentMethodType === PaymentMethods.Cash && (
                        <Typography variant="h5" fontWeight={600}>
                            Venta en efectivo
                        </Typography>
                    )}
                    {paymentMethodType === PaymentMethods.CreditCard && (
                        <Typography variant="h5" fontWeight={600}>
                            Venta con tarjeta
                        </Typography>
                    )}
                    {paymentMethodType === PaymentMethods.BankTransfer && (
                        <Typography variant="h5" fontWeight={600}>
                            Venta con transferencia
                        </Typography>
                    )}
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Divider />
                    {orders.map((order, index) => (
                        <Stack key={index} direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography variant="body1">{order.customer?.profile?.name}</Typography>
                            </Stack>
                            {paymentMethodType === PaymentMethods.Cash && (
                                <Typography variant="body1">{`$${getOrderPaidBy(order, PaymentMethod.Cash).toFixed(
                                    2
                                )}`}</Typography>
                            )}
                            {paymentMethodType === PaymentMethods.CreditCard && (
                                <Typography variant="body1">{currencyFormatter(getOrderPaidBy(
                                    order,
                                    PaymentMethod.CreditCard
                                ))}</Typography>
                            )}
                        </Stack>
                    ))}
                    {orders.length === 0 && <Typography variant="body1">No hay datos</Typography>}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
